import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import '../../../style/auditoria_cargas.css'
import apiService from '../../../actions/services';

function AuditoriaCargas_Salas() {
  const [cargas, setCargas] = useState([]);
  const [cargasFiltradas, setCargasFiltradas] = useState([]);
  const [cargaSeleccionada, setCargaSeleccionada] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    sala: '',
    usuario: '',
    fechaInicio: '',
    fechaFin: '',
    codigoEjecucion: '',
    productosMin: '',
    productosMax: ''
  });
  
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const SAL = {};
      const ejemploBitacora = await apiService.get("ObtenerCargasInventarios", SAL);
      const datos = JSON.parse(ejemploBitacora.datos_Generados);
      const cargasData = datos.CARGAS || [];
      setCargas(cargasData);
      setCargasFiltradas(cargasData);
      
      // Seleccionar la primera carga por defecto si existe
      if (cargasData.length > 0) {
        setCargaSeleccionada(cargasData[0]);
      }
    } catch (error) {
      console.error('Error al obtener las cargas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatearFecha = (fechaISO) => {
    if (!fechaISO) return '';
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatearFechaCompacta = (fechaISO) => {
    if (!fechaISO) return '';
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
  };

  const formatearFechaParaInput = (fechaISO) => {
    if (!fechaISO) return '';
    const fecha = new Date(fechaISO);
    return fecha.toISOString().split('T')[0];
  };

  const formatearNumero = (numero) => {
    if (!numero && numero !== 0) return '0';
    return parseFloat(numero).toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleSeleccionarCarga = (carga) => {
    setCargaSeleccionada(carga);
  };

  const handleChangeFiltro = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const aplicarFiltros = () => {
    let resultado = [...cargas];
    
    // Filtro por sala
    if (filtros.sala) {
      resultado = resultado.filter(carga => 
        carga.CodigoSala.toLowerCase().includes(filtros.sala.toLowerCase())
      );
    }
    
    // Filtro por usuario
    if (filtros.usuario) {
      resultado = resultado.filter(carga => 
        carga.Usuario.toLowerCase().includes(filtros.usuario.toLowerCase())
      );
    }
    
    // Filtro por código de ejecución
    if (filtros.codigoEjecucion) {
      resultado = resultado.filter(carga => 
        carga.CodigoEjecucion.toString().includes(filtros.codigoEjecucion)
      );
    }
    
    // Filtro por fecha inicio
    if (filtros.fechaInicio) {
      const fechaInicio = new Date(filtros.fechaInicio);
      resultado = resultado.filter(carga => 
        new Date(carga.FechaHoraEjecucion) >= fechaInicio
      );
    }
    
    // Filtro por fecha fin
    if (filtros.fechaFin) {
      const fechaFin = new Date(filtros.fechaFin);
      fechaFin.setHours(23, 59, 59, 999); // Para incluir todo el día
      resultado = resultado.filter(carga => 
        new Date(carga.FechaHoraEjecucion) <= fechaFin
      );
    }
    
    // Filtro por productos mínimos
    if (filtros.productosMin) {
      resultado = resultado.filter(carga => 
        carga.ProductosCargados >= parseInt(filtros.productosMin)
      );
    }
    
    // Filtro por productos máximos
    if (filtros.productosMax) {
      resultado = resultado.filter(carga => 
        carga.ProductosCargados <= parseInt(filtros.productosMax)
      );
    }
    
    setCargasFiltradas(resultado);
    
    // Si la carga seleccionada ya no está en los filtros, seleccionar la primera
    if (!resultado.some(carga => carga.CodigoEjecucion === cargaSeleccionada?.CodigoEjecucion)) {
      setCargaSeleccionada(resultado.length > 0 ? resultado[0] : null);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      sala: '',
      usuario: '',
      fechaInicio: '',
      fechaFin: '',
      codigoEjecucion: '',
      productosMin: '',
      productosMax: ''
    });
    setCargasFiltradas(cargas);
    
    // Seleccionar la primera carga si existe
    if (cargas.length > 0) {
      setCargaSeleccionada(cargas[0]);
    }
  };

  const exportarExcel = () => {
    if (!cargaSeleccionada || !cargaSeleccionada.DETALLE) return;
    
    const datosParaExcel = cargaSeleccionada.DETALLE.map(item => ({
      'Número Fila': item.NumeroFila,
      'Código Producto': item.CodigoProducto,
      'Nombre Producto': item.NombreProducto,
      'Existencia': item.Existencia
    }));

    const ws = XLSX.utils.json_to_sheet(datosParaExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle Carga');
    
    const nombreArchivo = `Detalle_Carga_${cargaSeleccionada.CodigoEjecucion}_${cargaSeleccionada.CodigoSala}.xlsx`;
    XLSX.writeFile(wb, nombreArchivo);
  };

  // Obtener lista única de salas para el dropdown
  const salasUnicas = [...new Set(cargas.map(carga => carga.CodigoSala).filter(Boolean))].sort();
  const usuariosUnicos = [...new Set(cargas.map(carga => carga.Usuario).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="carga-inventario loading-container">
        <div className="spinner"></div>
        <p>Cargando información...</p>
      </div>
    );
  }

  return (
    <div className="carga-inventario">
      <header className="header">
        <h1>Auditoría de Carga de Inventario</h1>
        <p>Revisar cada carga que realizan las salas</p>
      </header>

      {/* Panel de Filtros */}
      <div className="filtros-panel">
        <div className="filtros-header">
          <h3>Filtros</h3>
          <button 
            className="btn-toggle-filtros"
            onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
          >
            {mostrarFiltrosAvanzados ? 'Ocultar filtros avanzados' : 'Mostrar filtros avanzados'}
          </button>
        </div>
        
        <div className="filtros-basicos">
          <div className="filtro-group">
            <label>Sala:</label>
            <select 
              name="sala" 
              value={filtros.sala}
              onChange={handleChangeFiltro}
              className="filtro-select"
            >
              <option value="">Todas las salas</option>
              {salasUnicas.map(sala => (
                <option key={sala} value={sala}>{sala}</option>
              ))}
            </select>
          </div>
          
          <div className="filtro-group">
            <label>Usuario:</label>
            <select 
              name="usuario" 
              value={filtros.usuario}
              onChange={handleChangeFiltro}
              className="filtro-select"
            >
              <option value="">Todos los usuarios</option>
              {usuariosUnicos.map(usuario => (
                <option key={usuario} value={usuario}>{usuario}</option>
              ))}
            </select>
          </div>
          
          <div className="filtro-group">
            <label>Código Ejecución:</label>
            <input
              type="text"
              name="codigoEjecucion"
              value={filtros.codigoEjecucion}
              onChange={handleChangeFiltro}
              placeholder="Ej: 161"
              className="filtro-input"
            />
          </div>
        </div>
        
        {mostrarFiltrosAvanzados && (
          <div className="filtros-avanzados">
            <div className="filtro-row">
              <div className="filtro-group">
                <label>Fecha Inicio:</label>
                <input
                  type="date"
                  name="fechaInicio"
                  value={filtros.fechaInicio}
                  onChange={handleChangeFiltro}
                  className="filtro-input"
                  max={formatearFechaParaInput(new Date())}
                />
              </div>
              
              <div className="filtro-group">
                <label>Fecha Fin:</label>
                <input
                  type="date"
                  name="fechaFin"
                  value={filtros.fechaFin}
                  onChange={handleChangeFiltro}
                  className="filtro-input"
                  max={formatearFechaParaInput(new Date())}
                />
              </div>
            </div>
            
            <div className="filtro-row">
              <div className="filtro-group">
                <label>Productos Mín:</label>
                <input
                  type="number"
                  name="productosMin"
                  value={filtros.productosMin}
                  onChange={handleChangeFiltro}
                  placeholder="Mínimo"
                  min="0"
                  className="filtro-input"
                />
              </div>
              
              <div className="filtro-group">
                <label>Productos Máx:</label>
                <input
                  type="number"
                  name="productosMax"
                  value={filtros.productosMax}
                  onChange={handleChangeFiltro}
                  placeholder="Máximo"
                  min="0"
                  className="filtro-input"
                />
              </div>
            </div>
          </div>
        )}
        
        <div className="filtros-acciones">
          <button 
            className="btn-aplicar"
            onClick={aplicarFiltros}
          >
            Aplicar Filtros
          </button>
          
          <button 
            className="btn-limpiar"
            onClick={limpiarFiltros}
          >
            Limpiar Filtros
          </button>
          
          <div className="resultados-info">
            <span>Mostrando {cargasFiltradas.length} de {cargas.length} cargas</span>
          </div>
        </div>
      </div>

      <div className="row-container">
        <div className="columna izquierda">
          <main className="main-content">
            <div className="section-header">
              <h2>Cargas Realizadas</h2>
              <p className="subtitle">Seleccione una carga para ver su detalle</p>
            </div>
            
            {/* Tabla de Cargas */}
            <div className="tabla-container">
              <table className="tabla-cargas">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Sala</th>
                    <th>Usuario</th>
                    <th>Fecha</th>
                    <th>Productos</th>
                    <th>Unidades</th>
                  </tr>
                </thead>
                <tbody>
                  {cargasFiltradas.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="sin-resultados">
                        No se encontraron cargas con los filtros aplicados
                      </td>
                    </tr>
                  ) : (
                    cargasFiltradas.map((carga) => (
                      <tr 
                        key={carga.CodigoEjecucion}
                        className={`fila-carga ${cargaSeleccionada?.CodigoEjecucion === carga.CodigoEjecucion ? 'seleccionada' : ''}`}
                        onClick={() => handleSeleccionarCarga(carga)}
                      >
                        <td className="col-codigo">
                          <span className="codigo-badge">#{carga.CodigoEjecucion}</span>
                        </td>
                        <td className="col-sala">
                          <span className={`badge-sala ${carga.CodigoSala ? '' : 'sin-sala'}`}>
                            {carga.CodigoSala || 'N/A'}
                          </span>
                        </td>
                        <td className="col-usuario">
                          {carga.Usuario || 'N/A'}
                        </td>
                        <td className="col-fecha">
                          <div className="fecha-compacta">
                            {formatearFechaCompacta(carga.FechaHoraEjecucion)}
                          </div>
                        </td>
                        <td className="col-productos">
                          <span className="numero-productos">
                            {carga.ProductosCargados || 0}
                          </span>
                        </td>
                        <td className="col-unidades">
                          <span className="numero-unidades">
                            {formatearNumero(carga.ProductosCargadosUnidades)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </main>
        </div>
        
        <div className="columna derecha">
          <main className="main-content">
            <div className="section-header compacta">
              <div className="header-left">
                <h2>Detalle de Carga</h2>
                {cargaSeleccionada && (
                    
                  <div className="carga-info-header">
                    <span className="carga-info-item">
                      <strong>#</strong> {cargaSeleccionada.CodigoEjecucion}
                    </span>
                    <span className="carga-info-item">
                      <strong>Sala:</strong> {cargaSeleccionada.CodigoSala}
                    </span>
                    <span className="carga-info-item">
                      <strong>Usuario:</strong> {cargaSeleccionada.Usuario}
                    </span>
                    <span className="carga-info-item">
                      <strong>Fecha:</strong> {formatearFechaCompacta(cargaSeleccionada.FechaHoraEjecucion)}
                    </span>
                  </div>
                )}
              </div>
              <div className="header-right">
                {cargaSeleccionada && (
                  <button 
                    className="btn-exportar"
                    onClick={exportarExcel}
                    disabled={!cargaSeleccionada?.DETALLE?.length}
                  >
                    Exportar Excel
                  </button>
                )}
              </div>
            </div>
            
            {cargaSeleccionada ? (
              cargaSeleccionada.DETALLE && cargaSeleccionada.DETALLE.length > 0 ? (
                <div className="detalle-container">
                  {/* Tabla de Detalle Compacta */}
                  <div className="table-container compacta">
                    <table className="detalle-table compacta">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Código</th>
                          <th>Producto</th>
                          <th>Existencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cargaSeleccionada.DETALLE.map((item) => (
                          <tr key={item.NumeroFila}>
                            <td className="numero-fila col-codigo_det">
                                <span className="codigo-badge">
                                    {item.NumeroFila}
                                </span>
                            </td>

                            <td >
                              {item.CodigoProducto || <span className="sin-dato">-</span>}
                            </td>
                            <td >
                              {item.NombreProducto || <span className="sin-dato">-</span>}
                            </td>
                            <td className="existencia">
                              {formatearNumero(item.Existencia)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Footer con totales */}
                  <div className="detalle-footer">
                    <div className="totales-info">
                      <span className="total-item">
                        <span className="total-label">Productos:</span>
                        <span className="total-value">{cargaSeleccionada.ProductosCargados}</span>
                      </span>
                      <span className="total-item">
                        <span className="total-label">Unidades:</span>
                        <span className="total-value">{formatearNumero(cargaSeleccionada.ProductosCargadosUnidades)}</span>
                      </span>
                    </div>
                    <div className="guid-info">
                      <span className="guid-label">GUID:</span>
                      <span className="guid-value" title={cargaSeleccionada.GuidEjecucion}>
                        {cargaSeleccionada.GuidEjecucion?.substring(0, 8)}...
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state-detalle">
                  <p>No hay detalles disponibles para esta carga</p>
                </div>
              )
            ) : (
              <div className="empty-state-detalle">
                <p>Seleccione una carga para ver su detalle</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default AuditoriaCargas_Salas;